import { IconPlus } from '@tabler/icons-react'
import { useState } from 'react'

import { Dialog } from '../components/Dialog'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import type { Role, User, UserStatus } from '../lib/apiTypes'
import { formatDateTimeWib } from '../lib/format'
import { describeError, hasFieldError } from '../lib/describeError'
import { useCreateUser, useMerchants, useUpdateUser, useUsers } from '../lib/hooks'
import { roleLabel, rowStatusLabel, rowStatusTone } from '../lib/status'

/**
 * Pengelolaan pengguna konsol. Halaman ini hanya untuk admin: viewer tidak
 * melihat menunya, dan server menolak seluruh /users untuk viewer dengan 403.
 */
export function UsersPage() {
  const users = useUsers()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Pengguna</h1>
          <p className="muted">
            Admin boleh semua aksi, viewer hanya membaca. Menonaktifkan pengguna atau
            mengganti passwordnya langsung mencabut seluruh sesi aktif pengguna itu.
          </p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          <IconPlus size={18} stroke={1.75} aria-hidden="true" />
          Buat pengguna
        </button>
      </div>

      {users.isPending ? (
        <LoadingState what="daftar pengguna" />
      ) : users.isError ? (
        <ErrorState
          title="Gagal memuat daftar pengguna"
          error={users.error}
          onRetry={() => void users.refetch()}
          retrying={users.isFetching}
          hint="Halaman ini hanya untuk admin. Akun viewer ditolak server dengan kode 403."
        />
      ) : (users.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Belum ada pengguna konsol"
          body="Pengguna pertama dibuat lewat CLI di server. Setelah itu, pengguna berikutnya bisa dibuat dari halaman ini."
        />
      ) : (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Tabel pengguna, geser horizontal dengan tombol panah">
          <table className="table">
            <caption className="visually-hidden">
              Daftar pengguna konsol dengan peran, status, dan waktu masuk terakhir
            </caption>
            <thead>
              <tr>
                <th scope="col">Pengguna</th>
                <th scope="col">Peran</th>
                <th scope="col">Status</th>
                <th scope="col">Masuk terakhir</th>
                <th scope="col">Tindakan</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.data.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="fact__value">{row.name}</span>
                    <br />
                    <span className="caption mono">{row.email}</span>
                  </td>
                  <td>{roleLabel(row.role)}</td>
                  <td>
                    <Badge tone={rowStatusTone(row.status)}>{rowStatusLabel(row.status)}</Badge>
                  </td>
                  <td className="num">
                    {row.last_login_at ? formatDateTimeWib(row.last_login_at) : 'belum pernah masuk'}
                  </td>
                  <td className="is-actions">
                    <button type="button" className="btn btn--sm" onClick={() => setEditing(row)}>
                      Ubah
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? <CreateUserDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <EditUserDialog user={editing} onClose={() => setEditing(null)} /> : null}
    </>
  )
}

function MerchantScopeEditor({ restricted, onRestricted, selected, onSelected }: {
  restricted: boolean
  onRestricted: (value: boolean) => void
  selected: string[]
  onSelected: (value: string[]) => void
}) {
  const merchants = useMerchants()
  return <div className="field">
    <span className="field__label">Cakupan merchant</span>
    <div className="radio-group">
      <label><input type="radio" name="merchant-scope" checked={!restricted} onChange={() => onRestricted(false)} />Tanpa pembatasan: semua merchant</label>
      <label><input type="radio" name="merchant-scope" checked={restricted} onChange={() => onRestricted(true)} />Terbatas: hanya merchant yang dipilih</label>
    </div>
    {restricted ? <>
      <p className="field__hint">Tanpa pilihan berarti tidak memiliki akses data merchant. Client, kredensial, dan audit global tidak tersedia untuk viewer terbatas.</p>
      {merchants.isPending ? <LoadingState what="merchant untuk cakupan pengguna" /> : merchants.isError ? <ErrorState title="Gagal memuat pilihan merchant" error={merchants.error} onRetry={() => void merchants.refetch()} /> : !merchants.data?.data.length ? <p>Belum ada merchant. Cakupan terbatas kosong menolak semua data merchant.</p> : <div className="check-list">
        {merchants.data.data.map((merchant) => <label className="checkbox" key={merchant.id}><input type="checkbox" checked={selected.includes(merchant.id)} onChange={() => onSelected(selected.includes(merchant.id) ? selected.filter((id) => id !== merchant.id) : [...selected, merchant.id])} />{merchant.name} ({merchant.slug})</label>)}
      </div>}
      {selected.filter((id) => !merchants.data?.data.some((merchant) => merchant.id === id)).map((id) => <label className="checkbox" key={id}><input type="checkbox" checked onChange={() => onSelected(selected.filter((item) => item !== id))} />Merchant tersimpan: <span className="mono">{id}</span></label>)}
    </> : <p className="field__hint">Viewer boleh membaca semua merchant, termasuk merchant yang dibuat kemudian.</p>}
  </div>
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateUser()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('viewer')
  const [restricted, setRestricted] = useState(true)
  const [merchantIDs, setMerchantIDs] = useState<string[]>([])

  return (
    <Dialog
      title="Buat pengguna"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={
              create.isPending ||
              email.trim() === '' ||
              name.trim() === '' ||
              password.length < 12
            }
            onClick={() =>
              create.mutate(
                { email: email.trim(), name: name.trim(), password, role, merchant_scope_restricted: role === 'viewer' && restricted, merchant_ids: role === 'viewer' && restricted ? merchantIDs : [] },
                { onSuccess: onClose, onSettled: () => setPassword('') },
              )
            }
          >
            {create.isPending ? 'Menyimpan...' : 'Simpan pengguna'}
          </button>
        </>
      }
    >
      {create.error ? <Alert tone="danger">{describeError(create.error)}</Alert> : null}

      <div className="field">
        <label className="field__label" htmlFor="u-email">
          Email
        </label>
        <input
          id="u-email"
          className="input"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-invalid={hasFieldError(create.error, 'email')}
        />
        <span className="field__hint">Alamat ini dipakai untuk masuk ke konsol.</span>
        <FieldErrorText error={create.error} field="email" />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="u-name">
          Nama
        </label>
        <input
          id="u-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={hasFieldError(create.error, 'name')}
        />
        <FieldErrorText error={create.error} field="name" />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="u-password">
          Password awal
        </label>
        <input
          id="u-password"
          className="input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-describedby="u-password-hint"
          aria-invalid={hasFieldError(create.error, 'password')}
        />
        <span className="field__hint" id="u-password-hint">
          Minimal 12 karakter. Sampaikan lewat saluran yang aman, lalu minta pengguna
          menggantinya.
        </span>
        <FieldErrorText error={create.error} field="password" />
      </div>

      <div className="field">
        <span className="field__label">Peran</span>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="role"
              value="viewer"
              checked={role === 'viewer'}
              onChange={() => setRole('viewer')}
            />
            Viewer: hanya membaca, tanpa akses halaman Pengguna
          </label>
          <label>
            <input
              type="radio"
              name="role"
              value="admin"
              checked={role === 'admin'}
              onChange={() => setRole('admin')}
            />
            Admin: semua aksi, termasuk mengelola pengguna dan kredensial
          </label>
        </div>
      </div>
      {role === 'viewer' ? <MerchantScopeEditor restricted={restricted} onRestricted={setRestricted} selected={merchantIDs} onSelected={setMerchantIDs} /> : <p className="muted">Admin selalu memiliki akses tanpa pembatasan merchant.</p>}
      <FieldErrorText error={create.error} field="merchant_ids" />
    </Dialog>
  )
}

function EditUserDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const update = useUpdateUser(user.id)
  const [name, setName] = useState(user.name)
  const [role, setRole] = useState<Role>(user.role)
  const [restricted, setRestricted] = useState(user.merchant_scope_restricted)
  const [merchantIDs, setMerchantIDs] = useState<string[]>(user.merchant_ids ?? [])
  const [status, setStatus] = useState<UserStatus>(user.status)
  const [password, setPassword] = useState('')

  return (
    <Dialog
      title={`Ubah ${user.email}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={update.isPending || (password.length > 0 && password.length < 12)}
            onClick={() =>
              update.mutate(
                {
                  name: name.trim(),
                  role,
                  status,
                  ...(password ? { password } : {}),
                  merchant_scope_restricted: role === 'viewer' && restricted,
                  merchant_ids: role === 'viewer' && restricted ? merchantIDs : [],
                },
                { onSuccess: onClose, onSettled: () => setPassword('') },
              )
            }
          >
            {update.isPending ? 'Menyimpan...' : 'Simpan perubahan'}
          </button>
        </>
      }
    >
      {update.error ? <Alert tone="danger">{describeError(update.error)}</Alert> : null}

      <div className="field">
        <label className="field__label" htmlFor="eu-name">
          Nama
        </label>
        <input
          id="eu-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={hasFieldError(update.error, 'name')}
        />
        <FieldErrorText error={update.error} field="name" />
      </div>

      <div className="field">
        <span className="field__label">Peran</span>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="e-role"
              value="viewer"
              checked={role === 'viewer'}
              onChange={() => setRole('viewer')}
            />
            Viewer
          </label>
          <label>
            <input
              type="radio"
              name="e-role"
              value="admin"
              checked={role === 'admin'}
              onChange={() => setRole('admin')}
            />
            Admin
          </label>
        </div>
        <span className="field__hint">
          Server menolak menyisakan nol admin aktif: admin terakhir tidak bisa
          diturunkan menjadi viewer atau dinonaktifkan.
        </span>
        <FieldErrorText error={update.error} field="role" />
      </div>
      {role === 'viewer' ? <MerchantScopeEditor restricted={restricted} onRestricted={setRestricted} selected={merchantIDs} onSelected={setMerchantIDs} /> : <p className="muted">Admin selalu memiliki akses tanpa pembatasan merchant.</p>}
      <FieldErrorText error={update.error} field="merchant_ids" />

      <div className="field">
        <label className="field__label" htmlFor="eu-status">
          Status
        </label>
        <select
          id="eu-status"
          className="select"
          value={status}
          onChange={(event) => setStatus(event.target.value as UserStatus)}
        >
          <option value="ACTIVE">Aktif</option>
          <option value="DISABLED">Nonaktif</option>
        </select>
        <span className="field__hint">
          Menonaktifkan pengguna mencabut seluruh sesinya seketika.
        </span>
        <FieldErrorText error={update.error} field="status" />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="eu-password">
          Password baru
        </label>
        <input
          id="eu-password"
          className="input"
          type="password"
          autoComplete="new-password"
          placeholder="kosongkan bila tidak diganti"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-invalid={hasFieldError(update.error, 'password')}
        />
        <span className="field__hint">
          Minimal 12 karakter. Mengganti password mencabut sesi pengguna ini.
        </span>
        <FieldErrorText error={update.error} field="password" />
      </div>
    </Dialog>
  )
}
